import CustomInvoiceTable from "../../components/dataTable/CustomInvoiceTable";
import { AppLayout, ArrowBackIosIcon, Box, CustomConfirmDialog, dayjs, DatePicker, LocalizationProvider, AdapterDayjs, DeleteIcon, EditCalendarIcon, GridColDef, IconButton, InputAdornment, SearchIcon, TextField, Toaster, Typography, useApi, useEffect, useMemo, useNavigate, useParams, useState, CustomButton, CustomBackdrop, useLocation } from "../../utils/globalImports"
import type { Dayjs } from "dayjs";
import { headingStyles } from "../../assets/styles/CommonStyles";
import { InvoiceList, InvoiceListResponse } from "../../interfaces/Invoicing.interfaces";
import { Button, Chip, Stack, Tooltip } from "@mui/material";
import { DateFormat, InvoiceStatus } from "../../constants/constants";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

const statusChipConfig: Record<number, { label: string; bg: string; color: string; border: string }> = {
  [InvoiceStatus.Draft]: { label: 'Draft', bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  [InvoiceStatus.Submitted]: { label: 'Submitted', bg: '#fff8e1', color: '#e65100', border: '#ffcc80' },
  [InvoiceStatus.Approved]: { label: 'Approved', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  [InvoiceStatus.Rejected]: { label: 'Rejected', bg: '#fce4ec', color: '#c62828', border: '#ef9a9a' },
  [InvoiceStatus.Deleted]: { label: 'Deleted', bg: '#e1dede', color: '#757575', border: '#e0e0e0' }
};

const ALL_STATUSES = [
  InvoiceStatus.Draft,
  InvoiceStatus.Submitted,
  InvoiceStatus.Approved,
  InvoiceStatus.Rejected,
  InvoiceStatus.Deleted,
];

const sortOptions = [
  { value: "formName", text: "Form Name" },
  { value: "formType", text: "Form Type" },
]

const ViewInvoices: React.FC<any> = () => {
  const [selectedInvoice, setSelectedInvoice] = useState<{ invoiceId: number; status: InvoiceStatus } | null>(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const { execute, loading } = useApi();
  const [projectData, setProjectData] = useState<InvoiceList[]>([]);
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const [activeMonthYear, setActiveMonthYear] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() })
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const userId = localStorage.getItem('userId');
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const location = useLocation();
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [ProjectNumber, setProjectNumber] = useState<string>();
  const [projectName, setProjectName] = useState<string>();
  const isFiltered = searchText !== '' || selectedStatus != null || dateFrom !== null || dateTo !== null;
  const filterMonth = location.state?.filterMonth;
  const filterYear = location.state?.filterYear;

  const filteredData = useMemo(() => {
    return projectData.filter(item => {
      const itemDate = dayjs(item.dateCreated, "DD/MM/YYYY HH:mm");

      // Dashboard Period Filter

      if (searchText && !item.formName?.toLowerCase().includes(searchText.toLowerCase())) return false;
      if (selectedStatus !== null && item.status !== selectedStatus) return false;
      if (dateFrom || dateTo) {
        const itemDate = dayjs(item.dateCreated, DateFormat);
        if (dateFrom && itemDate.isBefore(dateFrom.startOf('day'))) return false;
        if (dateTo && itemDate.isAfter(dateTo.endOf('day'))) return false;
      }
      return true;
    });
  }, [projectData, searchText, selectedStatus, dateFrom, dateTo]);

  const handleStatusToggle = (status: InvoiceStatus) => {
    setSelectedStatus(prev => (prev === status ? null : status));
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedStatus(null);
    setDateFrom(null);
    setDateTo(null);
  };


  useEffect(() => {
    fetchInvoiceList();
  }, []);

  const fetchInvoiceList = async () => {
    try {
      if (!projectId) return;
      const { data } = await execute<InvoiceListResponse>('post', 'Invoice/InvoiceList', { projectId: Number(projectId), month: filterMonth, year: filterYear });
      setProjectNumber(data?.projectNumber)
      setProjectName(data?.projectName)
      setActiveMonthYear({
        month: data?.month ?? new Date().getMonth() + 1,
        year: data?.year ?? new Date().getFullYear()
      });

      const formattedData = (data?.invoice ?? []).map(item => ({
        ...item,
        dateCreated: item.dateCreated ? dayjs(item.dateCreated).format(DateFormat) : '',
      }));
      setProjectData(formattedData);
    } catch (ex) {
      console.log(ex);
    }
  };
  const handleViewNavigation = (invoiceId: number) => navigate(`/invoicing/view/${invoiceId}`,
    {
      state: {
        filterMonth: filterMonth,
        filterYear: filterYear,
      }
    }
  );
  const handleEditNavigation = (invoiceId: number) => navigate(`/invoicing/edit/${invoiceId}`,
    {
      state: {
        filterMonth: filterMonth,
        filterYear: filterYear,
      }
    }
  );
  const handleNavigateBack = () => navigate('/invoicing');

  const handleDeleteState = (invoiceId: number, status: InvoiceStatus) => {
    setSelectedInvoice({ invoiceId, status });
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedInvoice) return;
    await deleteInvoice();
    setDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
  };

  const deleteInvoice = async () => {
    const payload = { status: selectedInvoice?.status, userId };
    try {
      const { data } = await execute<boolean>('put', `Invoice/update-status/${selectedInvoice?.invoiceId}`, payload);
      if (data) {
        setToast({ open: true, message: 'Invoice Deleted Successfully', type: 'success' });
        fetchInvoiceList();
      } else {
        setToast({ open: true, message: 'Error Deleting Invoice', type: 'error' });
      }
    } catch (ex) {
      console.log(ex);
    }
  };

  const columns: GridColDef[] = [
    { field: 'formName', headerName: 'Form Name', flex: 1 },
    { field: 'formType', headerName: 'Form Type', flex: 1 },
    { field: 'dateCreated', headerName: 'Date Created', flex: 1 },
    { field: 'invoice', headerName: 'Invoice', flex: 0.8, filterable: false },
    {
      field: 'wipAccrual',
      headerName: 'WIP Accrual',
      flex: 0.9,
      filterable: false,

    },
    {
      field: 'wipRelease',
      headerName: 'WIP Release',
      flex: 0.9,
      filterable: false,

    },
    { field: 'netIntoAccounts', headerName: 'Net Into Accounts', flex: 1, filterable: false },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      valueGetter: (params) => InvoiceStatus[params],
      renderCell: (params) => {
        const statusNum = params.row.status as InvoiceStatus;
        const cfg = statusChipConfig[statusNum] ?? { label: String(statusNum), bg: '#f5f5f5', color: '#555', border: '#ccc' };
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              backgroundColor: cfg.bg,
              color: cfg.color,
              border: `1px solid ${cfg.border}`,
              fontWeight: 600,
              fontSize: '11px',
              height: '24px',
            }}
          />
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const rowDate = dayjs(params.row.dateCreated, DateFormat);

        const isDifferentMonth = !(filterMonth + 1 === activeMonthYear.month) || !(filterYear === activeMonthYear.year)

        const isLocked =
          params.row.status === InvoiceStatus.Submitted ||
          params.row.status === InvoiceStatus.Approved ||
          params.row.status === InvoiceStatus.Deleted || isDifferentMonth;

        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="View Invoice" arrow>
              <IconButton
                size="medium"
                onClick={() => handleViewNavigation(params.row.invoiceId)}
                sx={{ color: '#96c121', '&:hover': { backgroundColor: '#f0f7e0' } }}
              >
                <RemoveRedEyeIcon fontSize="medium" />
              </IconButton>
            </Tooltip>

            {!isLocked && (
              <Tooltip title="Edit Invoice" arrow>
                <IconButton
                  size="medium"
                  onClick={() => handleEditNavigation(params.row.invoiceId)}
                  sx={{ color: '#1e66ab', '&:hover': { backgroundColor: '#e3f0fb' } }}
                >
                  <EditCalendarIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}

            {!isLocked && (
              <Tooltip title="Delete Invoice" arrow>
                <IconButton
                  size="medium"
                  onClick={() => handleDeleteState(params.row.invoiceId, InvoiceStatus.Deleted)}
                  sx={{ color: '#c62828', '&:hover': { backgroundColor: '#fce4ec' } }}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <AppLayout>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
      }}>

        {/* ── Page Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Tooltip title="Back to Invoices" arrow>
            <IconButton
              onClick={handleNavigateBack}
              sx={{
                color: '#1e66ab',
                backgroundColor: '#e3f0fb',
                width: 36,
                height: 36,

                mb: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#cce0f5' },
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          <Box>
            <Typography sx={headingStyles}>
              {ProjectNumber} {projectName}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#888', fontFamily: 'Poppins', mt: -0.5 }}>
              {isFiltered
                ? `${filteredData.length} of ${projectData.length} invoice${projectData.length !== 1 ? 's' : ''}`
                : `${projectData.length} invoice${projectData.length !== 1 ? 's' : ''} found`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
          {/* ── Filters ── */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f5f8fc', border: '1px solid #e0eaf4' }}>
            <TextField
              size="small"
              placeholder="Search form name…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: 220, backgroundColor: '#fff', borderRadius: 1, '& .MuiInputBase-root': { fontFamily: 'Poppins', fontSize: 13 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#aaa' }} /></InputAdornment> } }}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={v => setDateFrom(v)}
                slotProps={{ textField: { size: 'small', sx: { width: 160, backgroundColor: '#fff', '& .MuiInputBase-root': { fontFamily: 'Poppins', fontSize: 13 } } } }}
              />
              <DatePicker
                label="To"
                value={dateTo}
                onChange={v => setDateTo(v)}
                minDate={dateFrom ?? undefined}
                slotProps={{ textField: { size: 'small', sx: { width: 160, backgroundColor: '#fff', '& .MuiInputBase-root': { fontFamily: 'Poppins', fontSize: 13 } } } }}
              />
            </LocalizationProvider>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto", // 2 columns
                alignItems: "center",
                gap: 1,
              }}
            >
              <Stack direction="row" flexWrap="wrap" spacing={0.5} >
                {ALL_STATUSES.map(s => {
                  const cfg = statusChipConfig[s];
                  const active = selectedStatus === s;
                  return (
                    <Chip
                      key={s}
                      label={cfg.label}
                      size="small"
                      onClick={() => handleStatusToggle(s)}
                      sx={{
                        cursor: 'pointer',
                        fontFamily: 'Poppins',
                        fontSize: '11px',
                        fontWeight: 600,
                        height: '26px',
                        backgroundColor: active ? cfg.bg : '#fff',
                        color: active ? cfg.color : '#888',
                        border: `1px solid ${active ? cfg.border : '#ccc'}`,
                        '&:hover': { backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` },
                      }}
                    />
                  );
                })}
              </Stack>
              {(selectedInvoices?.length > 0) && <CustomButton
                onClick={() =>
                  navigate('/invoiceList/viewinvoices', {
                    state: { selectedInvoices }
                  }
                  )}
              >View All (Selected {selectedInvoices?.length ?? 0})
              </CustomButton>}
            </Box>

            {isFiltered && (
              <Chip
                label="Clear"
                size="small"
                onClick={handleClearFilters}
                sx={{ cursor: 'pointer', fontFamily: 'Poppins', fontSize: '11px', fontWeight: 600, height: '26px', backgroundColor: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a', '&:hover': { backgroundColor: '#f8bbd0' } }}
              />
            )}
          </Box>

          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* ── Data Table ── */}
            <CustomInvoiceTable
              sortOptions={sortOptions}
              rows={filteredData}
              columns={columns}
              setSelectedInvoices={setSelectedInvoices}
              // gridHeight="600px"
              checkBox={true}
              onRowClick={(id) => handleViewNavigation(id)}
            />
          </Box>
        </Box>


      </Box>
      <CustomBackdrop open={loading} />
      <CustomConfirmDialog
        open={isDialogOpen}
        title="Delete Draft Invoice"
        message="Are you sure you want to delete this Invoice?"
        onConfirm={() => handleConfirm?.()}
        onCancel={() => handleCancel?.()}
        onClose={() => handleCancel?.()}
      />
      {/* <CustomBackdrop open={loading} /> */}

      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as 'success' | 'error'}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </AppLayout>
  );
};

export default ViewInvoices;
